import { Dialog, DialogContent, TextField, Box, Autocomplete } from "@mui/material";

import isEqual from "lodash/isEqual";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Props, State } from "./interfaces";
import { FieldsContainer } from "./styles";
import ConfirmationChangesDialog from "../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../containers/DialogTitle/DialogTitle";

import LoadingOverlay from "../../../../containers/LoadingOverlay/LoadingOverlay";

import { CandidateSTO } from "../../../../models/recruiting/candidate/candidate-sto";
import { PositionStatusRef } from "../../../../models/recruiting/positions/position-status/position-status-ref";
import AddEditButton from "../../../../reusable-components/AddEditButton/AddEditButton";
import AutocompleteDropdown from "../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { saveCandidate } from "../../../../services/recruiting/candidate/candidate-api.service";
import { PositionStatusesApiService } from "../../../../services/recruiting/positions/position-statuses/position-statuses-api.service";
import { POSITION_CANDIDATE_STATUSES } from "../../../../utils/constants/constants";

export const AddEditCandidateCvStatus = ({
  effect = "add",
  asLink = false,
  linkText,
  item,
  handleNotifyParent,
  buttonVariant,
  children,
  position,
  currCollection,
  currCollectionName,
  currIndex,
}: Props) => {
  const { t } = useTranslation();

  const DEFAULT_ITEM: Partial<CandidateSTO> = useMemo(() => {
    return {};
  }, [item]);

  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      loading: false,
      positionStatus: (POSITION_CANDIDATE_STATUSES[currCollectionName] as PositionStatusRef) ?? null,
      candidate: item ?? (DEFAULT_ITEM as CandidateSTO),
      isCCDOpen: false,
      areFieldsValidated: effect === "edit",
    };
  }, [effect, DEFAULT_ITEM, item, POSITION_CANDIDATE_STATUSES, currCollectionName]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const [nameOfCollection, setNameOfCollection] = useState("");

  const handleChangeCvStatusField = (_: any, value: PositionStatusRef) => {
    const namecOfCurrCollection = Object.keys(POSITION_CANDIDATE_STATUSES).find(
      (key) => POSITION_CANDIDATE_STATUSES[key as keyof typeof POSITION_CANDIDATE_STATUSES].id === value?.id
    );

    !!namecOfCurrCollection && setNameOfCollection(namecOfCurrCollection as string);

    setState((state: State) => ({
      ...state,
      positionStatus: value,
    }));
  };

  const handleSave = useCallback(async () => {
    setState((state: State) => ({ ...state, loading: true }));

    try {
      (currIndex === 0 || currIndex) && currCollection.splice(currIndex, 1);

      const response = await saveCandidate({
        ...state.candidate,
        [currCollectionName]: currCollection,
        [nameOfCollection as string]: [...(state.candidate as any)[nameOfCollection], position],
      });

      if (!response.error) {
        effect === "add" && setState({ ...INITIAL_STATE });
        effect === "edit" &&
          setState((state: State) => ({
            ...state,
            isDialogOpen: false,
            loading: false,
            isCCDOpen: false,
          }));
        handleNotifyParent(true);
      }
    } catch (error) {
      setState((state: State) => ({
        ...state,
        loading: false,
      }));
    }
  }, [INITIAL_STATE, effect, handleNotifyParent, state.candidate, currCollectionName, nameOfCollection, position]);

  const handleDiscardChanges = useCallback(() => {
    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE]);

  const resetAndCloseDialog = (): void => {
    if (
      !isEqual(state.candidate, INITIAL_STATE.candidate) ||
      !isEqual(state.positionStatus?.id, INITIAL_STATE.positionStatus?.id)
    ) {
      setState((state: State) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };

  const areFieldsValidated = useCallback((): boolean => {
    return (
      !!state.positionStatus &&
      POSITION_CANDIDATE_STATUSES[currCollectionName as keyof typeof POSITION_CANDIDATE_STATUSES].id !==
        state.positionStatus?.id
    );
  }, [state.positionStatus, currCollectionName]);

  useEffect(() => {
    setState((state: State) => ({
      ...state,
      areFieldsValidated: areFieldsValidated(),
    }));
  }, [state.positionStatus, areFieldsValidated]);

  useEffect(() => {
    !!item &&
      setState((state: State) => ({
        ...state,
        candidate: item,
      }));

    return () => {
      if (effect === "add") setState({ ...INITIAL_STATE });
    };
  }, [item, effect, INITIAL_STATE]);

  return (
    <>
      {children ? (
        <div
          onClick={() => {
            setState((state: State) => ({ ...state, isDialogOpen: true }));
          }}
        >
          {children}
        </div>
      ) : (
        <AddEditButton
          title={t(`buttons.${effect}`)}
          effect={effect}
          asLink={asLink}
          linkText={linkText}
          onClick={() => {
            setState((state: State) => ({ ...state, isDialogOpen: true }));
          }}
          {...(buttonVariant === "text" && { buttonText: "Edit" })}
        />
      )}
      <Dialog
        open={state.isDialogOpen}
        onClose={resetAndCloseDialog}
        fullWidth
      >
        <LoadingOverlay loading={state.loading}>
          <DialogTitle
            title={"Change canidate status"}
            handleClose={resetAndCloseDialog}
          />
          <DialogContent>
            <FieldsContainer>
              <Box sx={{ marginTop: "10px" }}>
                {position && (
                  <Autocomplete
                    aria-required={true}
                    disabled
                    value={position}
                    getOptionLabel={(option) => option.positionCode ?? ""}
                    options={[position]}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={"Candidate"}
                      />
                    )}
                  />
                )}
                <AutocompleteDropdown
                  labelKey={"CV status"}
                  serviceApi={new PositionStatusesApiService()}
                  field="positionStatus"
                  handleChange={handleChangeCvStatusField}
                  selectedValue={state.positionStatus ?? null}
                  required
                />
              </Box>
            </FieldsContainer>
          </DialogContent>
          <DialogActions
            handleSave={handleSave}
            disabledSaveButton={!areFieldsValidated()}
            handleCancel={resetAndCloseDialog}
          />
        </LoadingOverlay>
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
      />
    </>
  );
};
